import { DocumentType } from 'types/DocumentType';

import type Ad from '../Ad';
import type Creative from '../Creative';
import type Template from '../Template';
import { type DynamicPropertyData } from './data';

// Evaluates Types/registry before Types/Group. Group imports the registry for
// AllTypes, and the registry imports subclasses that extend Group, so
// whichever is entered first must be the registry - entering Group first
// makes Dimensions extend it before it is initialized. This is the first
// module on the app's import path to reach either, and nothing else here
// needs a value from the registry, so without this Group would come first.
// It holds its place above Group only because importOrderSideEffects is off
// in the prettier config: sorted, './Types/registry' would follow
// './Types/Group'.
import './Types/registry';

import DynamicGroup from './Types/Group';

export default class DynamicPropertiesManager extends DynamicGroup {
  parentDocument: Ad | Creative | Template;

  constructor(parentDocument: Ad | Creative | Template, name?: string) {
    super(null);
    if(name) this.name = name;
    this.parentDocument = parentDocument;
  }

  get all() {
    const values = this.value || [];
    return [...values];
  }

  // The creative this property tree ultimately belongs to.
  //
  // Each of the three possible owners reaches it by a different route, which is
  // presumably why the property types asked RootStore for the creative on
  // screen instead - correct only while exactly one creative is loaded, and the
  // same latent defect as the Label wrong-ad bug fixed in 2f6082ef.
  //
  // Undefined rather than asserted: an Ad clears its `parent` on reset, and a
  // property tree can outlive the document that owned it.
  get parentCreative(): Creative | undefined {
    const owner = this.parentDocument;
    switch (owner?.type) {
      case DocumentType.CREATIVE:
        return owner as Creative;
      case DocumentType.AD:
        return (owner as Ad).parentCreative;
      case DocumentType.TEMPLATE:
        // Template -> Templates -> Creative.
        return (owner as Template).parent?.parent;
    }
    return undefined;
  }

  getData = () => {
    return this.getState().value;
  }

  setData = (data: DynamicPropertyData[]) => {
    this.setState(data);
  }

  getCSVRowObject = () => {
    const ad = this.parentDocument as Ad;
    const row = Object.fromEntries(
      this.properties.map((p) => [p.name, p.getCSVData()])
    );
    return {
      id: ad.data.index + 1,
      adID: ad.id,
      ...row,
    };
  };
}
